/**
 * THE ONE STALENESS GUARD — shared by schema strings and component strings.
 * =======================================================================
 *
 * `translate.ts` documents *why* a stale translation is refused rather than rendered; this file is the
 * *single implementation* of that decision, extracted so the component-string layer cannot grow a
 * second copy of it. Two copies of a safety guard is one guard and one liability: the day someone
 * "simplifies" the copy that has no tests pointed at it, blocking validation messages start rendering
 * fluent, confident, superseded French and nothing goes red.
 *
 * There are exactly five outcomes and four of them render the English source. See `translate.ts` for
 * the argument; the difference here is that the unit of resolution is a **pattern** rather than a
 * string, because a plural message is a set of forms and the count that chooses between them is not
 * known until the render call. Resolution decides *which language's pattern is trustworthy*;
 * `message.ts` then decides *which form of it to show*. Keeping those separate is what lets the
 * staleness check be identical for both kinds of string.
 */

import { SOURCE_LOCALE } from './locales.ts';
import type { Locale } from './locales.ts';
import type { LocaleCatalogue, LocaleEntry } from './catalog/types.ts';
import { isPluralPattern, patternForms, PLURAL_CATEGORY_ORDER } from './message.ts';
import type { MessagePattern, PluralForms } from './message.ts';

export type Resolution = 'translated' | 'fallback-missing' | 'fallback-stale' | 'source' | 'unknown-key';

/** The authored English for one key, plus the hash a catalogue entry must match. */
export interface SourceRecord {
  readonly pattern: MessagePattern;
  /** `hashSource(canonicalSource(pattern))`. */
  readonly hash: string;
}

/** How a key is turned into its English. Schema keys and `ui.*` keys use different lookups. */
export type SourceLookup = (key: string) => SourceRecord | undefined;

export interface ResolvedPattern {
  readonly key: string;
  /** The pattern to render. English unless `resolution` is `translated`. */
  readonly pattern: MessagePattern;
  readonly resolution: Resolution;
  /** The authored English, whatever `pattern` ended up being. Lets a review surface show both. */
  readonly source: MessagePattern;
}

export function zeroCounts(): Record<Resolution, number> {
  return { translated: 0, 'fallback-missing': 0, 'fallback-stale': 0, source: 0, 'unknown-key': 0 };
}

/**
 * Turn a catalogue entry into a pattern, given what the English looks like.
 *
 * The three cases, and why each is right:
 *
 *  · `entry.forms` present → the translator supplied plural forms. `other` falls back to `entry.text`
 *    so a catalogue can never present a plural entry with no renderable form.
 *  · English is plural, translation is a single string → **legitimate, not an error.** `ja-JP` has one
 *    plural category, so one sentence is the complete translation. It is expanded to `{ other }`.
 *  · English is a plain string → the translation is a plain string. `forms` on a non-plural entry is
 *    ignored rather than honoured, because a plural translation of a non-plural source is a translator
 *    error and silently switching the render path on it would hide it.
 */
export function entryPattern(entry: LocaleEntry, english: MessagePattern): MessagePattern {
  if (!isPluralPattern(english)) return entry.text;
  if (entry.forms === undefined) return { other: entry.text };
  const forms: Record<string, string> = {};
  for (const category of PLURAL_CATEGORY_ORDER) {
    const form = entry.forms[category];
    if (form !== undefined) forms[category] = form;
  }
  return { ...forms, other: entry.forms.other ?? entry.text } as PluralForms;
}

/**
 * Wrap every form of a pattern in `‹…›`. QA sweeps only — a technician must never see markup, and a
 * marker on a blocking message is one more thing to misread.
 */
export function markPattern(pattern: MessagePattern): MessagePattern {
  if (!isPluralPattern(pattern)) return `‹${pattern}›`;
  const marked: Record<string, string> = {};
  for (const category of PLURAL_CATEGORY_ORDER) {
    const form = pattern[category];
    if (form !== undefined) marked[category] = `‹${form}›`;
  }
  // `other` restated so the result is a `PluralForms` by construction rather than by assertion.
  return { ...marked, other: `‹${pattern.other}›` };
}

export interface ResolveArgs {
  readonly key: string;
  readonly lookup: SourceLookup;
  readonly locale: Locale;
  /** `null` for the source locale and for a chunk that failed to load — both mean "render English". */
  readonly catalogue: LocaleCatalogue | null;
  readonly mark?: boolean;
  /** Mutated in place when supplied, so a translator can report its own resolution mix. */
  readonly counts?: Record<Resolution, number>;
  /** Keys the English does not know. Populated when supplied. */
  readonly unknown?: Set<string>;
}

/** Decide which language's pattern is trustworthy for one key. The whole staleness guard. */
export function resolvePattern(args: ResolveArgs): ResolvedPattern {
  const { key, lookup, locale, catalogue, counts, unknown } = args;
  const mark = args.mark === true;
  const bump = (resolution: Resolution): void => {
    if (counts !== undefined) counts[resolution] += 1;
  };

  const english = lookup(key);
  if (english === undefined) {
    /*
     * Not a translation gap: caller and catalogue disagree about what exists. There is no English to
     * fall back to, so the key itself is shown — hiding it would replace a visible bug with an
     * invisible blank. Tests assert this path is unreachable from any key the app actually renders.
     */
    bump('unknown-key');
    unknown?.add(key);
    return { key, pattern: key, resolution: 'unknown-key', source: key };
  }

  if (locale === SOURCE_LOCALE || catalogue === null) {
    bump('source');
    return { key, pattern: english.pattern, resolution: 'source', source: english.pattern };
  }

  const entry = catalogue.entries[key];
  if (entry === undefined) {
    bump('fallback-missing');
    return {
      key,
      pattern: mark ? markPattern(english.pattern) : english.pattern,
      resolution: 'fallback-missing',
      source: english.pattern,
    };
  }

  if (entry.sourceHash !== english.hash) {
    // Translated against different English. Refused — see `translate.ts`.
    bump('fallback-stale');
    return {
      key,
      pattern: mark ? markPattern(english.pattern) : english.pattern,
      resolution: 'fallback-stale',
      source: english.pattern,
    };
  }

  const translated = entryPattern(entry, english.pattern);
  /*
   * A translation that is present, fresh, and EMPTY is worse than a missing one: it renders a blank
   * question with a required asterisk. It is treated as missing rather than trusted.
   */
  if (patternForms(translated).every((form) => form.trim().length === 0)) {
    bump('fallback-missing');
    return {
      key,
      pattern: mark ? markPattern(english.pattern) : english.pattern,
      resolution: 'fallback-missing',
      source: english.pattern,
    };
  }

  bump('translated');
  return { key, pattern: translated, resolution: 'translated', source: english.pattern };
}
